<template>
  <div>
    <div class="tile my-4 px-3 py-4">
      <flow-group :flowGroup="flowGroup"/>
    </div>

    <div class="tile my-4">
      <loading-view :loading="initialLoadingFlows">
        <div>
          <form-input-header :header="`${application1.application.name} (${flow1.name})`"></form-input-header>
          <div v-if="!firstFlowHasEndpoints()" class="help-text">
            {{ trans('dashboard.integrations.flows.endpoints.no_endpoints') }}
          </div>

          <div v-for="(endpoint, idx) in firstApplicationEndpoints" :key="`flow1-${idx}`" class="w-full items-center">
            <form-input
              :key="`${endpoint.name}-${idx}`"
              v-model="endpoint.value"
              :label="endpoint.name"
              :required="true"
              :disabled="!canUpdate"
              type="text"
            >
              <template v-slot:extra-input>
                <select class="form-control form-select" v-model="endpoint.method" :disabled="!canUpdate">
                  <option v-for="(method, idx) in methods" :key="idx" :value="method">{{ method }}</option>
                </select>

                <a v-if="canUpdate"
                   class="d-inline-block mt-2 px-1 cursor-pointer"
                   @click="openTestEndpointModal(application1.id, application1.application.name, endpoint)">
                  {{ trans('dashboard.integrations.flows.endpoints.test_endpoint') }}
                </a>
              </template>
            </form-input>
          </div>

        </div>

        <div>
          <form-input-header :header="`${application2.application.name} (${flow2.name})`"></form-input-header>
          <div v-if="!secondFlowHasEndpoints()" class="help-text">
            {{ trans('dashboard.integrations.flows.endpoints.no_endpoints') }}
          </div>

          <div class="w-full items-center" v-for="(endpoint, idx) in secondApplicationEndpoints" :key="`flow2-${idx}`">
            <form-input
              :key="`${endpoint.name}-${idx}`"
              v-model="endpoint.value"
              :label="endpoint.name"
              :required="true"
              :disabled="!canUpdate"
              type="text"
            >
              <template v-slot:extra-input>
                <select class="form-control form-select" v-model="endpoint.method" :disabled="!canUpdate">
                  <option v-for="(method, idx) in methods" :key="idx" :value="method">{{ method }}</option>
                </select>
              </template>
            </form-input>

            <a v-if="canUpdate"
               class="d-inline-block mt-2 px-1 cursor-pointer"
               @click="openTestEndpointModal(application2.id, application2.application.name, endpoint)">
              {{ trans('Test Endpoint') }}
            </a>
          </div>
        </div>

        <div v-if="$can('flows.endpoints.update') && canUpdate" class="flex items-center my-6 mr-3 ml-1">
          <div class="ml-auto">
            <manual-link-button :manual-link="manualLink"/>
            <form-button
              :processing="processingBackToDefault"
              :value="trans('dashboard.integrations.flows.back_to_default')"
              type="button"
              @click.native="showConfirmationModal = true"
            />

            <form-button
              :value="trans('common.save')"
              :processing-value="trans('common.saving')"
              :processing="processingSave"
              class="mr-6"
              type="submit"
              @click.native="saveEndpoints"
            />
          </div>
        </div>
      </loading-view>

      <flow-endpoint-test-modal v-if="canUpdate"
                                :show="showTestEndpointModal"
                                :user-integration-id="userIntegration.id"
                                :user-integration-flow-id="userIntegrationFlow.id"
                                :selected-endpoint="selectedEndpoint"
                                @close="closeTestEndpointModal"/>

      <confirmation-modal
        :confirm-button-text="trans('common.confirm')"
        :content="trans('dashboard.integrations.flows.endpoints.back_to_default_confirmation_description')"
        :header="trans('dashboard.integrations.flows.confirm_action')"
        :show-modal="showConfirmationModal"
        @close="showConfirmationModal = false"
        @confirm="backToDefault"
      />
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import Swal from 'sweetalert2';
import DashboardLayout from '@/Layouts/DashboardLayout';
import IntegrationFlowLayout from '@/Layouts/IntegrationFlowLayout';
import FlowGroup from '@/components/Dashboard/FlowGroup';
import FormInput from '@/components/Dashboard/FormInput';
import FormInputHeader from '@/components/Dashboard/FormInputHeader';
import FormButton from '@/components/Shared/Form/Button';
import ManualLinkButton from '@/components/Shared/ManualLinkButton.vue';
import FlowEndpointTestModal from "@/Pages/Dashboard/Integrations/Flows/Partials/FlowEndpointTestModal.vue";
import ConfirmationModal from "@/components/Dashboard/ConfirmationModal.vue";

export default {
  name: 'FlowEndpoints',

  layout: [DashboardLayout, IntegrationFlowLayout],

  components: {
    ConfirmationModal,
    FlowEndpointTestModal,
    ManualLinkButton,
    FormButton,
    FlowGroup,
    FormInput,
    FormInputHeader,
  },

  props: {
    manualLink: String,
  },

  data() {
    return {
      initialLoadingFlows: true,

      firstApplicationEndpoints: [],

      secondApplicationEndpoints: [],

      processingSave: false,

      processingBackToDefault: false,

      methods: ['GET', 'POST', 'PUT', 'PATCH', 'MERGE', 'DELETE'],

      showTestEndpointModal: false,

      showConfirmationModal: false,

      selectedEndpoint: null,
    };
  },

  async beforeMount() {
    await this.prepareData();
  },

  computed: {
    ...mapGetters('flowsDetails', ['isAdmin', 'isPartner', 'flowGroup', 'userIntegration', 'userIntegrationFlow']),

    application1() {
      return this.userIntegration.first_application;
    },

    application2() {
      return this.userIntegration.second_application;
    },

    flow1() {
      return this.userIntegrationFlow.first_flow;
    },

    flow2() {
      return this.userIntegrationFlow.second_flow;
    },

    canUpdate() {
      return this.isAdmin || this.isPartner;
    },
  },

  methods: {
    async prepareData() {
      await axios
        .get(`/api/integrations/${this.userIntegration.id}/flows/${this.userIntegrationFlow.id}/endpoints`)
        .then(({data}) => {
          this.firstApplicationEndpoints = data.data.firstApplicationEndpoints;
          this.secondApplicationEndpoints = data.data.secondApplicationEndpoints;
        })
        .catch(({response}) => this.handleError(response.data));
      this.initialLoadingFlows = false;
    },

    openTestEndpointModal(applicationId, applicationName, endpoint) {
      this.selectedEndpoint = {
        user_application_id: applicationId,
        applicationName: applicationName,
        name: endpoint.name,
        endpoint: endpoint.value,
        method: endpoint.method,
      }

      this.showTestEndpointModal = true
    },

    closeTestEndpointModal() {
      this.showTestEndpointModal = false
      this.selectedEndpoint = null
    },

    async saveEndpoints() {
      this.processingSave = true;
      await axios
        .patch(`/api/integrations/${this.userIntegration.id}/flows/${this.userIntegrationFlow.id}/endpoints`, {
          endpoints_1: this.firstApplicationEndpoints,
          endpoints_2: this.secondApplicationEndpoints,
        })
        .then(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.success_update_endpoints'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });
        })
        .catch(({response}) => this.handleError(response.data));

      this.processingSave = false;
    },

    async backToDefault() {
      this.processingBackToDefault = true;

      await axios
        .delete(`/api/integrations/${this.userIntegration.id}/flows/${this.userIntegrationFlow.id}/endpoints`)
        .then(({data}) => {
          this.firstApplicationEndpoints = data.data.firstApplicationEndpoints;
          this.secondApplicationEndpoints = data.data.secondApplicationEndpoints;

          Swal.fire({
            text: this.trans('dashboard.alerts.success_revert_endpoints'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });
        })
        .catch(({response}) => this.handleError(response.data))
        .finally(() => (this.processingBackToDefault = false));
    },

    handleError(error) {
      Swal.fire({
        text: error.message,
        icon: 'error',
        showConfirmButton: false,
        timer: 2000,
      });
    },

    firstFlowHasEndpoints() {
      return this.firstApplicationEndpoints.length > 0;
    },

    secondFlowHasEndpoints() {
      return this.secondApplicationEndpoints.length > 0;
    },
  },
};
</script>
